import { useEffect, useState, type FormEvent } from 'react'
import { CalendarClock, CircleAlert, Crown, Edit3, Plus, QrCode, ShieldCheck, Sparkles, Trash2 } from 'lucide-react'
import { api, brl, integer } from '../api'
import { Empty, Loading } from '../components/Loading'
import { Modal } from '../components/Modal'
import type { Plan } from '../types'
import { PageHead } from './DashboardPage'

// Precisa espelhar PLAN_DURATIONS na Pages Function.
const PLAN_DURATIONS = [
  { months: 1, label: 'Mensal', short: 'MENSAL' },
  { months: 3, label: 'Trimestral', short: 'TRIMESTRAL' },
  { months: 6, label: 'Semestral', short: 'SEMESTRAL' },
  { months: 12, label: 'Anual', short: 'ANUAL' },
  { months: 24, label: 'Bienal (2 anos)', short: '2 ANOS' },
]

function durationLabel(months: number) {
  return PLAN_DURATIONS.find(option => option.months === months)?.label || 'Anual'
}

const NEW_PLAN: Plan = {
  id: '', name: '', description: '', active: true, featured: false, annual_price: 0, installments: 0,
  duration_months: 12, pix_discount_percent: 0, renewal_discount_percent: 0, trial_days: 0, wallet_limit: 0, operation_limit: 0,
  report_limits: { monthly: 0, annual: 0, ir_national: 0, ir_foreign: 0 },
}

const percent = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

function pixPrice(plan: Pick<Plan, 'annual_price' | 'pix_discount_percent'>) {
  const discount = Math.min(Math.max(plan.pix_discount_percent || 0, 0), 100)
  return plan.annual_price * (1 - discount / 100)
}

export function PlansPage() {
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [editing, setEditing] = useState<Plan | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Plan | null>(null)
  const [error, setError] = useState('')

  const load = () => api<{ plans: Plan[] }>('/plans')
    .then(data => { setPlans(data.plans); setError('') })
    .catch(reason => setError(reason.message))

  useEffect(() => { void load() }, [])

  if (error && !plans) return <div className="notice error"><CircleAlert/>{error}</div>
  if (!plans) return <Loading label="Carregando configuração dos planos…"/>

  const orderedPlans = [...plans].sort((left, right) =>
    left.annual_price - right.annual_price || left.name.localeCompare(right.name, 'pt-BR')
  )

  return <>
    <PageHead
      eyebrow="PRODUTO E ASSINATURAS"
      title="Planos"
      text="Cadastre, edite e exclua preços, limites e disponibilidade."
      action={<button type="button" className="primary" onClick={() => setCreating(true)}><Plus/>Cadastrar plano</button>}
    />
    <div className="notice info"><ShieldCheck/><span>As alterações de preço, duração e limites serão aplicadas ao catálogo de assinaturas. Revise os valores antes de salvar.</span></div>
    {error && <div className="notice error"><CircleAlert/>{error}</div>}
    {orderedPlans.length ? <section className="plan-admin-grid" aria-label="Planos por ordem de preço">
      {orderedPlans.map(plan => <article className={`plan-admin-card coupon-style plan-list-card ${plan.featured ? 'recommended' : ''} ${plan.active ? '' : 'inactive'}`} key={plan.id}>
        <div className="plan-list-summary">
          <span className="plan-card-icon">{plan.featured ? <Crown/> : <Sparkles/>}</span>
          <div className="plan-card-heading">
            <div className="plan-card-meta"><small>{plan.active ? 'PLANO ATIVO' : 'PLANO INATIVO'}</small>{plan.featured && <span className="recommended-badge"><Crown aria-hidden="true"/> MAIS INDICADO</span>}{plan.pix_discount_percent > 0 && <span className="pix-badge"><QrCode aria-hidden="true"/>{percent.format(plan.pix_discount_percent)}% NO PIX</span>}</div>
            <h2>{plan.name}</h2>
            <code>{plan.id}</code>
          </div>
          <PlanPrice plan={plan}/>
          <div className="plan-card-actions">
            <button type="button" className="icon-button" onClick={() => setEditing(plan)} title="Editar plano" aria-label={`Editar ${plan.name}`}><Edit3/></button>
            <button type="button" className="icon-button danger" onClick={() => setDeleting(plan)} title="Excluir plano" aria-label={`Excluir ${plan.name}`}><Trash2/></button>
          </div>
        </div>
        <dl className="plan-list-limits">
          <div><dt>Desconto Pix</dt><dd className={plan.pix_discount_percent > 0 ? 'highlight' : ''}>{plan.pix_discount_percent > 0 ? `${percent.format(plan.pix_discount_percent)}%` : 'Sem desconto'}</dd></div>
          <div><dt>Carteiras</dt><dd>{integer.format(plan.wallet_limit)}</dd></div>
          <div><dt>Operações</dt><dd>{integer.format(plan.operation_limit)}</dd></div>
          <div><dt>Relatórios mensais</dt><dd>{integer.format(plan.report_limits.monthly)}</dd></div>
          <div><dt>Relatórios anuais</dt><dd>{integer.format(plan.report_limits.annual)}</dd></div>
          <div><dt>Resumos de IR nacional</dt><dd>{integer.format(plan.report_limits.ir_national)}</dd></div>
          <div><dt>Resumos de IR estrangeiro</dt><dd>{integer.format(plan.report_limits.ir_foreign)}</dd></div>
          {plan.renewal_discount_percent > 0 && <div><dt>Desconto de renovação</dt><dd className="highlight">{percent.format(plan.renewal_discount_percent)}%</dd></div>}
        </dl>
        <footer><span className={`status-label ${plan.active ? 'good' : 'muted'}`}><i/>{plan.active ? 'Disponível' : 'Indisponível'}</span><small>Catálogo de assinaturas</small></footer>
      </article>)}
    </section> : <section className="panel"><Empty>Nenhum plano cadastrado.</Empty></section>}
    {creating && <PlanModal plan={NEW_PLAN} mode="create" close={() => setCreating(false)} saved={() => { setCreating(false); void load() }}/>}
    {editing && <PlanModal plan={editing} mode="edit" close={() => setEditing(null)} saved={() => { setEditing(null); void load() }}/>}
    {deleting && <DeletePlanModal plan={deleting} close={() => setDeleting(null)} deleted={() => { setDeleting(null); void load() }}/>}
  </>
}

// A parcela é o valor que o cliente compara na hora de escolher, então ela fica
// em destaque e o total do período vai para a legenda.
function PlanPrice({ plan }: { plan: Plan }) {
  const installment = plan.installments > 0 && plan.annual_price > 0 ? plan.annual_price / plan.installments : 0
  const caption = installment
    ? `${durationLabel(plan.duration_months)} · total de ${brl.format(plan.annual_price)}`
    : `${durationLabel(plan.duration_months)}${plan.trial_days ? ` · ${plan.trial_days} dias de acesso inicial` : ''}`
  return <div className="plan-price">
    <strong className={plan.annual_price ? '' : 'no-price'}>
      {installment ? <><span className="plan-installment-count">{plan.installments}x</span>{brl.format(installment)}</> : plan.annual_price ? brl.format(plan.annual_price) : 'Sem cobrança'}
    </strong>
    <small>{caption}</small>
    {plan.annual_price > 0 && plan.pix_discount_percent > 0 && <small className="plan-pix-price">{brl.format(pixPrice(plan))} à vista no Pix</small>}
  </div>
}

function PlanModal({ plan, mode, close, saved }: { plan: Plan; mode: 'create' | 'edit'; close: () => void; saved: () => void }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // Guardar o texto digitado, e não o número, mantém o campo editável: converter a
  // cada tecla faz Number('') virar 0 e o zero volta sem poder ser apagado. Zero é
  // exibido como campo vazio com placeholder para não se somar ao que for digitado.
  const [price, setPrice] = useState(plan.annual_price ? String(plan.annual_price) : '')
  const [pixDiscount, setPixDiscount] = useState(plan.pix_discount_percent ? String(plan.pix_discount_percent) : '')
  const priceValue = Number(price) || 0
  const pixDiscountValue = Number(pixDiscount) || 0

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    const form = new FormData(event.currentTarget)
    const number = (name: string) => Number(form.get(name) || 0)
    const id = String(form.get('id') || plan.id).trim().toLowerCase()
    try {
      await api(`/plans/${encodeURIComponent(id)}`, {
        method: mode === 'create' ? 'POST' : 'PUT',
        body: {
          name: form.get('name'), active: form.get('active') === 'on', featured: form.get('featured') === 'on',
          annual_price: number('annual_price'), duration_months: number('duration_months'),
          pix_discount_percent: number('pix_discount_percent'),
          installments: number('installments'),
          trial_days: number('trial_days'), wallet_limit: number('wallet_limit'),
          operation_limit: number('operation_limit'),
          description: String(form.get('description') || ''),
          renewal_discount_percent: number('renewal_discount_percent'),
          report_limits: {
            monthly: number('reports_monthly'), annual: number('reports_annual'),
            ir_national: number('reports_ir_national'), ir_foreign: number('reports_ir_foreign'),
          },
        },
      })
      saved()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível salvar.')
    } finally {
      setSaving(false)
    }
  }

  return <Modal title={mode === 'create' ? 'Cadastrar plano' : `Editar ${plan.name}`} close={close}>
    <form className="form-grid" onSubmit={submit}>
      <label className="full">Identificador
        <input name="id" defaultValue={plan.id} disabled={mode === 'edit'} required pattern="[a-z0-9_-]{2,40}" placeholder="ex.: basico" autoComplete="off"/>
        {mode === 'create' && <small>Use letras minúsculas, números, hífen ou sublinhado.</small>}
      </label>
      <label className="full">Nome<input name="name" defaultValue={plan.name} required placeholder="Nome apresentado no painel"/></label>
      <label>
        <span className="field-label"><CalendarClock aria-hidden="true"/>Duração do plano</span>
        <select name="duration_months" defaultValue={String(plan.duration_months || 12)}>
          {PLAN_DURATIONS.map(option => <option key={option.months} value={option.months}>{option.label}</option>)}
        </select>
      </label>
      <label>Preço do período (R$)<input name="annual_price" type="number" min="0" step="0.01" value={price} onChange={event => setPrice(event.target.value)} placeholder="0"/></label>
      <label>Parcelas<input name="installments" type="number" min="0" defaultValue={plan.installments}/></label>
      <label className="full pix-discount-field">
        <span className="field-label"><QrCode aria-hidden="true"/>Desconto para pagamento via Pix (%)</span>
        <input name="pix_discount_percent" type="number" min="0" max="100" step="0.01" value={pixDiscount} onChange={event => setPixDiscount(event.target.value)} placeholder="0"/>
        <small>{pixDiscountValue > 0 && priceValue > 0
          ? `Pagando via Pix o cliente paga ${brl.format(pixPrice({ annual_price: priceValue, pix_discount_percent: pixDiscountValue }))} em vez de ${brl.format(priceValue)}.`
          : 'Use 0 para não oferecer desconto no Pix. O desconto será aplicado ao pagamento à vista.'}</small>
      </label>
      <label className="full">Descrição<input name="description" defaultValue={plan.description} maxLength={200} placeholder="Texto exibido no card do plano (opcional)"/></label>
      <label>Desconto de renovação (%)<input name="renewal_discount_percent" type="number" min="0" max="100" step="1" defaultValue={plan.renewal_discount_percent || 0}/><small>Aplicado quando o cliente renova o Premium. Use 0 para não oferecer.</small></label>
      <label>Dias do período de teste<input name="trial_days" type="number" min="0" defaultValue={plan.trial_days}/></label>
      <label>Limite de carteiras<input name="wallet_limit" type="number" min="0" defaultValue={plan.wallet_limit}/></label>
      <label>Limite de operações<input name="operation_limit" type="number" min="0" defaultValue={plan.operation_limit}/></label>
      <label>Relatórios mensais<input name="reports_monthly" type="number" min="0" defaultValue={plan.report_limits.monthly}/></label>
      <label>Relatórios anuais<input name="reports_annual" type="number" min="0" defaultValue={plan.report_limits.annual}/></label>
      <label>Resumos de IR nacional<input name="reports_ir_national" type="number" min="0" defaultValue={plan.report_limits.ir_national}/></label>
      <label>Resumos de IR estrangeiro<input name="reports_ir_foreign" type="number" min="0" defaultValue={plan.report_limits.ir_foreign}/></label>
      <label className="switch-row full"><input name="active" type="checkbox" defaultChecked={plan.active}/><span><strong>Plano disponível</strong><small>Permite que o plano seja oferecido no sistema.</small></span></label>
      <label className="switch-row full recommended-switch"><input name="featured" type="checkbox" defaultChecked={Boolean(plan.featured)}/><span><strong>Plano mais indicado</strong><small>Destaca este plano com cores especiais e o selo de destaque no aplicativo.</small></span></label>
      {error && <div className="form-error full" role="alert">{error}</div>}
      <div className="modal-actions full"><button type="button" className="secondary" onClick={close}>Cancelar</button><button type="submit" className="primary" disabled={saving}>{saving ? 'Salvando…' : mode === 'create' ? 'Cadastrar plano' : 'Salvar plano'}</button></div>
    </form>
  </Modal>
}

function DeletePlanModal({ plan, close, deleted }: { plan: Plan; close: () => void; deleted: () => void }) {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const remove = async () => {
    setWorking(true)
    setError('')
    try {
      await api(`/plans/${encodeURIComponent(plan.id)}`, { method: 'DELETE' })
      deleted()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível excluir.')
      setWorking(false)
    }
  }
  return <Modal title="Excluir plano" close={close}>
    <div className="confirm-content">
      <span className="confirm-icon danger"><Trash2/></span>
      <div><h3>Excluir {plan.name}?</h3><p>O documento será removido de <strong>billing_plans</strong>. Esta ação não altera automaticamente os clientes que já receberam esse identificador de plano.</p></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="modal-actions"><button type="button" className="secondary" onClick={close}>Cancelar</button><button type="button" className="danger-action" onClick={remove} disabled={working}>{working ? 'Excluindo…' : 'Excluir plano'}</button></div>
    </div>
  </Modal>
}

